import React, { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import StudentPicker from "@/components/StudentPicker";
import { ConflictPanel } from "@/components/ConflictPanel";

const pick = (t) => ({ student_id: t.student_id, mansione_id: t.mansione_id, date: t.date, start_time: t.start_time || "", end_time: t.end_time || "", note: t.note || "" });

export function TurnoEditDialog({ turno, onClose, students, mansioni }) {
  const qc = useQueryClient();
  const [form, setForm] = useState(null);
  const [warn, setWarn] = useState(null);
  const [force, setForce] = useState(false);
  const [confirmStep, setConfirmStep] = useState(false);
  useEffect(() => { if (turno) { setForm(pick(turno)); setWarn(null); setForce(false); setConfirmStep(false); } }, [turno]);

  const saveMut = useMutation({
    mutationFn: async () => (await api.patch(`/mansioni-area/turni/${turno.id}`, { ...form, force })).data,
    onSuccess: () => { toast.success("Turno aggiornato"); qc.invalidateQueries({ queryKey: ["turni"] }); qc.invalidateQueries({ queryKey: ["planner-calendar"] }); onClose(); },
    onError: (e) => {
      const raw = e.response?.data?.detail_raw;
      if (e.response?.status === 409 && raw?.conflicts) { setWarn(raw); setConfirmStep(false); } else toast.error(e.response?.data?.detail || "Errore");
    },
  });
  if (!turno || !form) return null;
  const orig = pick(turno);
  const changed = Object.keys(form).filter((k) => form[k] !== orig[k]);
  const sName = (id) => { const s = students.find((x) => x.id === id); return s ? `${s.first_name} ${s.last_name}` : "—"; };
  const mName = (id) => mansioni.find((m) => m.id === id)?.name || turno.mansione_name || "—";
  const LABEL = { student_id: "Studente", mansione_id: "Mansione", date: "Data", start_time: "Ora inizio", end_time: "Ora fine", note: "Note" };
  const show = (k, v) => (k === "student_id" ? sName(v) : k === "mansione_id" ? mName(v) : v || "—");

  return (
    <Dialog open={!!turno} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[92vh] overflow-y-auto" data-testid="turno-edit-dialog">
        <DialogHeader><DialogTitle>Modifica turno</DialogTitle></DialogHeader>
        {!confirmStep ? (
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Studente</Label><StudentPicker students={students} value={form.student_id} onChange={(id) => setForm({ ...form, student_id: id })} testId="turno-edit-student"/></div>
              <div><Label>Mansione</Label>
                <select value={form.mansione_id} onChange={(e) => setForm({ ...form, mansione_id: e.target.value })} data-testid="turno-edit-mansione" className="mt-1.5 h-9 w-full rounded-md border border-stone-200 bg-white px-2 text-sm">
                  {mansioni.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><Label>Data</Label><Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} className="mt-1.5" data-testid="turno-edit-date"/></div>
              <div><Label>Inizio</Label><Input type="time" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="mt-1.5" data-testid="turno-edit-start"/></div>
              <div><Label>Fine</Label><Input type="time" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} className="mt-1.5" data-testid="turno-edit-end"/></div>
            </div>
            <div><Label>Note</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} className="mt-1.5" data-testid="turno-edit-note"/></div>
            <ConflictPanel warn={warn} force={force} onForceChange={setForce} testIdPrefix="turno-edit"/>
          </div>
        ) : (
          <div className="space-y-2" data-testid="turno-edit-recap">
            <p className="text-sm text-stone-600">Verifica le modifiche prima di salvare:</p>
            <div className="rounded-lg border border-stone-200 divide-y divide-stone-100 text-sm">
              {changed.map((k) => (
                <div key={k} className="grid grid-cols-[100px_1fr] gap-2 px-3 py-2">
                  <span className="text-stone-500">{LABEL[k]}</span>
                  <span><s className="text-stone-400">{show(k, orig[k])}</s> → <b>{show(k, form[k])}</b></span>
                </div>
              ))}
            </div>
            {force && warn && <p className="text-xs text-amber-700">Salverai ignorando {warn.conflict_count} conflitti.</p>}
          </div>
        )}
        <DialogFooter>
          {confirmStep
            ? <Button variant="outline" onClick={() => setConfirmStep(false)} data-testid="turno-edit-back">Indietro</Button>
            : <Button variant="outline" onClick={onClose}>Annulla</Button>}
          {confirmStep ? (
            <Button className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={saveMut.isPending} onClick={() => saveMut.mutate()} data-testid="turno-edit-confirm">
              {saveMut.isPending ? "Salvataggio…" : "Conferma modifica"}
            </Button>
          ) : (
            <Button className="bg-[#1C352D] hover:bg-[#264A3E]" disabled={!changed.length || !form.student_id || !form.date || (warn && !force)} onClick={() => setConfirmStep(true)} data-testid="turno-edit-next">
              Continua ({changed.length} modifiche)
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
